<template>
  <div v-if='pageReady' id='pdf-ready'>
    <ExportCover preTitle='Historical Asset Manager Report' :date='new Date()' :title='assetManagerName' />
    <div class='print-page print-portrait x-fixed-y-auto print-padding'>
      <ExportPageHeading preTitle='Historical Asset Manager Report' :date='new Date()' :title='assetManagerName'>
        <h2>Filters Applied</h2>
      </ExportPageHeading>
      <ul class='mt-[5mm] text-sm divide-y border-t border-b'>
        <li class='py-1 text-xs'>
          Years<br />{{ state.filters.year.from }} – {{ state.filters.year.to }}
        </li>
      </ul>
    </div>
    <div class='print-page print-landscape x-auto-y-auto print-padding'>
      <ExportPageHeading preTitle='Historical Asset Manager Report' :date='new Date()' :title='assetManagerName'>
        <h2>Results</h2>
        <p>Scores for all engagements.</p>
      </ExportPageHeading>
      <div class='mt-8'>
        <GraphHistoricalPortfolioTable
          :params='params'
          :showOrganisation='isSuperAdminView()'
          :showAsset='true'
          :view='view'
          :isPrint='true'
        />
      </div>
    </div>
    <div class='print-page print-landscape x-fixed-y-auto print-padding'>
      <ExportPageHeading preTitle='Historical Asset Manager Report' :date='new Date()' :title='assetManagerName'>
        <h2>Theme Trends</h2>
        <p>Theme transformation year on year.</p>
      </ExportPageHeading>
      <div class='mt-8'>
        <GraphHistoricalThemeBar :params='params' :showKey='true' />
      </div>
    </div>
    <ExportDisclaimerPage preTitle='Historical Asset Manager Report' :date='new Date()' :title='assetManagerName' />
  </div>
</template>

<script setup>

import { computed, reactive, ref } from 'vue';

import { getStateFromRoute } from '@/router';
import { rehydrateCollections, getExportTitle } from '@/kernel/report';

import ExportCover from '@/components/export/export-cover.vue';
import ExportPageHeading from '@/components/export/export-page-heading.vue';
import ExportDisclaimerPage from '@/components/export/export-disclaimer-page.vue';

import GraphHistoricalPortfolioTable from '@/components/reports/graphs/graph-historical-portfolio-table.vue';
import GraphHistoricalThemeBar from '@/components/reports/graphs/graph-historical-theme-bar.vue';

import { getOrganisationId, isSuperAdminView, isOrganisationView } from '@/kernel/session';

const view = computed(() => {
  return isSuperAdminView() ? 'platform' : 'organisation';
});

const organisationId = computed(() => {
  return getOrganisationId();
});

const filterCollectionMap = [
  {key: 'organisationAssetManager', collection: 'organisation-asset-manager'},
  {key: 'platformAssetManager', collection: 'platform-asset-manager'}
];

const getDefaultState = (preload) => {
  return {
    tab: preload?.tab || 'results',
    filters: {
      year: {
        from: preload?.filters?.year?.from || new Date().getFullYear() - 3,
        to: preload?.filters?.year?.to || new Date().getFullYear()
      },
      organisationAssetManager: preload?.filters?.organisationAssetManager || null,
      platformAssetManager: preload?.filters?.platformAssetManager || null
    }
  }
}

const state = reactive(getDefaultState());
const pageReady = ref(false);
const routeState = getStateFromRoute();
const params = ref(null);

const assetManagerName = computed(() => {
  return state.filters.organisationAssetManager?.name || state.filters.platformAssetManager?.name;
});

const calcParams = () => {
  if (state.filters.year.from.toString().length !== 4) {
    return null;
  }

  if (state.filters.year.to.toString().length !== 4) {
    return null;
  }

  if (!state.filters.organisationAssetManager && !state.filters.platformAssetManager) {
    return null;
  }

  const args = {
    year: state.filters.year,
    organisationAssetManager: [state.filters.organisationAssetManager?.id] || null,
    platformAssetManager: [state.filters.platformAssetManager?.id] || null
  }

  if (isOrganisationView()) {
    args.organisation = [getOrganisationId()];
  }

  return args;
}

const loadState = async () => {
  if (routeState?.filters) {
    const defaultState = getDefaultState(routeState);

    const collectionFilters = await rehydrateCollections(filterCollectionMap, defaultState.filters);

    defaultState.filters = {
      ...defaultState.filters,
      ...collectionFilters
    }

    Object.assign(state, defaultState);
  }

  document.title = getExportTitle(`Historical Asset Manager Report - ${assetManagerName.value}`, new Date());

  params.value = calcParams();

  pageReady.value = true;
}

loadState();

</script>
